import { forwardRef } from 'react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ForwardedRef, ReactNode } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { radii } from '../../../../styles/onboardingUiTokens';
import { SvgIcon, normalizeSvgIconPreset } from '../icons/SvgIcon';
import type { ButtonIconPosition, ButtonSize, ButtonVariant } from './buttonTokens';
import { buttonSizeTokens } from './buttonTokens';

export type { ButtonIconPosition, ButtonSize, ButtonVariant } from './buttonTokens';

type CommonButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode | string;
  /** Icon placement relative to the label. Icon-only buttons ignore this visually. Default: `right`. */
  iconPosition?: ButtonIconPosition;
  disabled?: boolean;
};

type LabelProps = { children: ReactNode } | { icon: ReactNode | string; 'aria-label': string; children?: undefined };

type ButtonModeProps = CommonButtonProps &
  LabelProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | keyof CommonButtonProps> & {
    href?: undefined;
  };

type AnchorModeProps = CommonButtonProps &
  LabelProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | keyof CommonButtonProps | 'type'> & {
    href: string;
  };

export type ButtonProps = ButtonModeProps | AnchorModeProps;

function isAnchorModeProps(props: ButtonProps): props is AnchorModeProps {
  return typeof props.href === 'string' && props.href.length > 0;
}

export function hasVisibleLabel(children: React.ReactNode): boolean {
  if (children == null || typeof children === 'boolean') {
    return false;
  }
  if (typeof children === 'string') {
    return children.trim().length > 0;
  }
  if (typeof children === 'number') {
    return true;
  }
  if (Array.isArray(children)) {
    return children.some(hasVisibleLabel);
  }
  return true;
}

function getIconSvgColors(variant: ButtonVariant, disabled: boolean): { primary: string; secondary: string } {
  // Figma disabled-primary (ERS-61 A7): icon follows the text color, not the variant's usual white.
  if (variant === 'primary' && disabled) {
    const dimmed = getMediumRGBA(0.5);
    return { primary: dimmed, secondary: dimmed };
  }
  if (variant === 'primary' || variant === 'secondary') {
    return { primary: colors.white, secondary: colors.light };
  }
  return { primary: colors.dark, secondary: colors.medium };
}

function resolveIconVisual(icon: ReactNode | string, variant: ButtonVariant, disabled: boolean, iconBox: number): ReactNode {
  const { primary, secondary } = getIconSvgColors(variant, disabled);

  if (typeof icon === 'string') {
    const preset = normalizeSvgIconPreset(icon);
    if (preset) {
      return <SvgIcon name={preset} size={iconBox} primaryColor={primary} secondaryColor={secondary} />;
    }
    return (
      <LiteralIcon aria-hidden $box={iconBox}>
        {icon}
      </LiteralIcon>
    );
  }

  return (
    <CustomIconSlot aria-hidden $box={iconBox}>
      {icon}
    </CustomIconSlot>
  );
}

const LiteralIcon = styled.span<{ $box: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $box }) => $box}px;
  height: ${({ $box }) => $box}px;
  font-size: ${({ $box }) => Math.round($box * 0.85)}px;
  line-height: 1;
`;

const CustomIconSlot = styled.span<{ $box: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ $box }) => $box}px;
  height: ${({ $box }) => $box}px;
  line-height: 0;

  & > svg {
    width: ${({ $box }) => $box}px;
    height: ${({ $box }) => $box}px;
  }
`;

/** Wraps label + icons so busy stripes (`::before`, z-index 0) stay behind real content (raw text is not `> *`). */
const ButtonContents = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: inherit;
  min-width: 0;
  color: inherit;
  -webkit-text-fill-color: currentColor;
`;

const variantSurface = {
  primary: css`
    color: ${colors.white};
    background-color: ${colors.primary};
    border-color: ${colors.primary};

    &:hover:not(:disabled):not([aria-disabled='true']):not([aria-busy='true']) {
      filter: brightness(0.95);
    }
  `,
  secondary: css`
    color: ${colors.white};
    background-color: ${colors.dark};
    border-color: ${colors.dark};

    &:hover:not(:disabled):not([aria-disabled='true']):not([aria-busy='true']) {
      filter: brightness(1.08);
    }
  `,
  tertiary: css`
    color: ${colors.dark};
    background-color: ${colors.white};
    border-color: ${colors.medium};

    &:hover:not(:disabled):not([aria-disabled='true']):not([aria-busy='true']) {
      background-color: ${colors.light};
    }
  `,
  ghost: css`
    color: ${colors.dark};
    background-color: transparent;
    border-color: transparent;

    &:hover:not(:disabled):not([aria-disabled='true']):not([aria-busy='true']) {
      background-color: ${getMediumRGBA(0.1)};
    }
  `,
} satisfies Record<ButtonVariant, ReturnType<typeof css>>;

/**
 * Horizontal period for `repeating-linear-gradient(45deg, …, 0–8px, 8–16px)` so one full cycle
 * matches `translateX(-period)` and the animation loop is seamless (no “blitz” on wrap).
 */
const BUSY_STRIPE_PERIOD_X_PX = 16 * Math.SQRT2;

const busyStripeSlide = keyframes`
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(-${BUSY_STRIPE_PERIOD_X_PX}px, 0, 0);
  }
`;

const busyStripeStops: Record<ButtonVariant, { base: string; stripe: string }> = {
  primary: {
    base: colors.primary,
    stripe: `color-mix(in srgb, ${colors.primary} 88%, ${colors.black})`,
  },
  secondary: {
    base: colors.dark,
    stripe: `color-mix(in srgb, ${colors.dark} 78%, ${colors.white})`,
  },
  tertiary: {
    base: colors.white,
    stripe: `color-mix(in srgb, ${colors.white} 90%, ${colors.medium})`,
  },
  ghost: {
    base: getMediumRGBA(0.06),
    stripe: getMediumRGBA(0.12),
  },
};

type StyledControlProps = {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $iconOnly: boolean;
};

const controlStyles = css<StyledControlProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-height: ${({ $size }) => buttonSizeTokens[$size].minHeight}px;
  padding: 0 ${({ $size }) => buttonSizeTokens[$size].paddingX}px;
  border-style: solid;
  border-width: 2px;
  border-radius: ${radii.control};
  font-family: inherit;
  font-weight: 600;
  font-size: ${({ $size }) => buttonSizeTokens[$size].fontSize};
  line-height: ${({ $size }) => buttonSizeTokens[$size].lineHeight};
  gap: ${({ $size, $iconOnly }) => ($iconOnly ? 0 : buttonSizeTokens[$size].gap)}px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease,
    filter 0.15s ease,
    opacity 0.1s ease;

  ${({ $variant }) => variantSurface[$variant]}

  &:active:not(:disabled):not([aria-disabled='true']):not([aria-busy='true']) {
    transform: scale(0.99);
  }

  &:disabled:not([aria-busy='true']),
  &[aria-disabled='true']:not([aria-busy='true']) {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: none;
    transform: none;
  }

  ${({ $variant }) =>
    $variant === 'primary' &&
    css`
      /* Figma disabled-primary (ERS-61 A7): explicit colors instead of opacity over the surface. */
      &:disabled:not([aria-busy='true']),
      &[aria-disabled='true']:not([aria-busy='true']) {
        opacity: 1;
        background-color: ${colors.light};
        border-color: ${colors.light};
        color: ${getMediumRGBA(0.5)};
      }
    `}

  &[aria-busy='true'] {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    pointer-events: none;
    cursor: wait;
    transform: none;
    opacity: 0.76;
    -webkit-text-fill-color: currentColor;
  }

  &[aria-busy='true']::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: calc(100% + ${BUSY_STRIPE_PERIOD_X_PX}px);
    z-index: 0;
    border-radius: inherit;
    will-change: transform;
    ${({ $variant }) => {
      const { base, stripe } = busyStripeStops[$variant];
      return css`
        background-image: repeating-linear-gradient(45deg, ${base} 0, ${base} 8px, ${stripe} 8px, ${stripe} 16px);
        animation: ${busyStripeSlide} 1.45s linear infinite;
      `;
    }}
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

const StyledButton = styled.button<StyledControlProps>`
  ${controlStyles}
`;

const StyledAnchor = styled.a<StyledControlProps>`
  ${controlStyles}
  text-decoration: none;
`;

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(props, ref) {
  if (isAnchorModeProps(props)) {
    const {
      href,
      onClick,
      variant = 'secondary',
      size = 'small',
      iconPosition = 'right',
      icon,
      children,
      disabled,
      className,
      ...anchorDomRest
    } = props;

    const hasLabel = hasVisibleLabel(children);
    const iconOnly = Boolean(icon) && !hasLabel;
    const iconBox = buttonSizeTokens[size].iconBox;
    const isDisabled = Boolean(disabled);

    const iconNode = icon != null && icon !== false ? resolveIconVisual(icon, variant, isDisabled, iconBox) : null;

    const contents = (
      <ButtonContents>
        {iconPosition === 'left' && iconNode}
        {hasLabel ? children : null}
        {iconPosition === 'right' && iconNode}
      </ButtonContents>
    );

    return (
      <StyledAnchor
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        href={href}
        aria-disabled={isDisabled ? true : undefined}
        tabIndex={isDisabled ? -1 : undefined}
        className={className}
        $variant={variant}
        $size={size}
        $iconOnly={iconOnly}
        onClick={(event) => {
          if (isDisabled) {
            event.preventDefault();
          }
          onClick?.(event);
        }}
        {...anchorDomRest}
      >
        {contents}
      </StyledAnchor>
    );
  }

  const {
    type = 'button',
    variant = 'secondary',
    size = 'small',
    iconPosition = 'right',
    icon,
    children,
    disabled,
    className,
    ...rest
  } = props;

  const hasLabel = hasVisibleLabel(children);
  const iconOnly = Boolean(icon) && !hasLabel;
  const iconBox = buttonSizeTokens[size].iconBox;
  const isDisabled = Boolean(disabled);

  const iconNode = icon != null && icon !== false ? resolveIconVisual(icon, variant, isDisabled, iconBox) : null;

  const contents = (
    <ButtonContents>
      {iconPosition === 'left' && iconNode}
      {hasLabel ? children : null}
      {iconPosition === 'right' && iconNode}
    </ButtonContents>
  );

  return (
    <StyledButton
      ref={ref as ForwardedRef<HTMLButtonElement>}
      type={type}
      disabled={isDisabled}
      className={className}
      $variant={variant}
      $size={size}
      $iconOnly={iconOnly}
      {...rest}
    >
      {contents}
    </StyledButton>
  );
});

Button.displayName = 'Button';
